import MarkdownIt from 'markdown-it';

/**
 * Shared MarkdownIt instance with consistent configuration
 */
const md = new MarkdownIt({ 
  html: true, // Allow HTML tags
  linkify: false, // Don't auto-linkify (we'll handle that separately if needed)
  breaks: true // Convert line breaks to <br>
});

/**
 * Detects if text contains markdown syntax
 * @param text - The text to check
 * @returns true if markdown syntax is detected
 */
export const hasMarkdown = (text: string): boolean => {
  if (!text) return false;
  
  // Check for bullet points (must come before inline italic to avoid conflicts)
  const hasList = /^\s*[-*+]\s+/m.test(text);
  
  // Check for other markdown patterns
  const hasOtherMarkdown = /^#{1,6}\s|^\d+\.\s|^>\s|```|^\|.*\|$|^\s{2,}\d+\.\s|\*\*[^*]+\*\*|(?<!\*)\*[^*\n]+\*(?!\*)/m.test(text);
  
  return hasList || hasOtherMarkdown;
};

/**
 * Converts markdown text to HTML
 * @param markdown - The markdown text to convert
 * @param options - Optional configuration
 * @param options.normalizeLineEndings - Whether to normalize line endings (default: true)
 * @returns HTML string
 */
export const markdownToHtml = (
  markdown: string,
  options: { normalizeLineEndings?: boolean } = {}
): string => {
  if (!markdown) return '';
  
  const { normalizeLineEndings = true } = options;
  
  // Normalize line endings to ensure markdown-it can parse lists correctly
  const normalizedText = normalizeLineEndings
    ? markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    : markdown;
  
  return md.render(normalizedText);
};

/**
 * Converts markdown to HTML if markdown syntax is detected, otherwise returns the text as-is
 * @param text - The text to convert (may or may not contain markdown)
 * @param options - Optional configuration
 * @returns HTML string if markdown detected, otherwise original text
 */
export const convertMarkdownToHtmlIfNeeded = (
  text: string,
  options: { normalizeLineEndings?: boolean } = {}
): string => {
  if (!text) return '';
  
  if (hasMarkdown(text)) {
    return markdownToHtml(text, options);
  }
  
  // If no markdown, just convert newlines to <br> tags
  return text.replace(/\n/g, '<br>');
};

/**
 * Flattens HTML paragraph structure to <br> so \n\n shows as one blank line and \n as one line break.
 * Use when passing HTML to compose editor or anywhere that should not rely on <p> margins.
 * @param html - HTML string (e.g. from markdownToHtml)
 * @returns HTML with </p><p> and similar replaced by <br><br>, and wrapper <p>/</p> removed
 */
export const flattenParagraphsToBr = (html: string): string => {
  if (!html || typeof html !== 'string') return html;
  return html
    .replace(/<\/p>\s*<p[^>]*>/gi, '<br><br>')
    .replace(/<\/p>\s*<(ul|ol|h[1-6]|blockquote|pre|div)/gi, '<br><br><$1')
    .replace(/^<p[^>]*>/i, '')
    .replace(/<\/p>/g, '');
};

